import { distance } from "@turf/turf";
import { generateRandomPoints } from "../utils/generateRandomPoints";
import { getClosestTimestamp } from "../utils/timeUtils";
import Bacteria from "./Bacteria";

/**
 * パラメータ
 */
const stationCoverageRadius = 300; // Stop勢圏 (m) -->> Creatureでの核の大きさに該当する
const defaultOpacity = 0.75; // 菌描画時の透過率
const maxAmplitude = 5; // 膨らむ最大値 (m)
const period = 60 * 4; // 栄養到達の認知において変化を表現する秒数(GlobalTimerの経過時間) 
const varianceOfDivideNutrients = 1; // 栄養分配の散らし方のパラメータ ※0で均等分配
const maxHealthPoint = 20; // 菌の最大の大きさを30mになるよう調整する
const basicalyLifeSeconds = 60 * 20; // 菌の基準寿命（秒）
const basicalyStepOfArriving = 90; // 基準到着間隔（秒）

/**
 * 駅・停留所を表現するクラス
 */
class Stop {
    #stop_id; // stop_id
    #stop_name;
    #route_id;
    route_name;
    #arrivalTimeStamps = []; // 到着Tripsの時刻
    #innerTimer;
    #capacityOfBacterias = 10; // Stop内の許容菌生息数
    #nutrientInStop = 0; // Stop内に存在する栄養量
    #distributionRate = 0.1; // 上記の分配率
    #metabolizingNutrient = maxHealthPoint / basicalyLifeSeconds; // 基準栄養消費量（1秒あたり当たりどれほど栄養を消費するか？）
    /** 
     * 1tripが供給する栄養量 
     * 基準間隔にてキャパ数の50%が生きられるように設定する．
     */
    #defaultNutrient = ((maxHealthPoint / 2) * (this.#capacityOfBacterias / 2) / basicalyLifeSeconds) * basicalyStepOfArriving;

    #minimumInitialNutrient = maxHealthPoint * 0.75; // 菌発生に必要な最小の栄養量
    #initialNoOfBacterias = Math.round(this.#capacityOfBacterias / 5); // 初期で存在する菌数（キャパの2割）    
    #lastTripTimeStampIdx; // 前回到着したtripのindex
    
    /**
     * コンストラクタ
     * @param {number} latitude 
     * @param {number} longitude 
     * @param {string} stop_id
     * @param {string} stop_name
     * @param {string} route_id
     * @param {string} route_name 
     * @param {string} route_color
     * @param {Array} arrivalTimeStamps 
     * @param currentTimeStamp
     */
    constructor(latitude,longitude,stop_id,stop_name,route_id,route_name,route_color,route_type,arrivalTimeStamps,currentTimeStamp) {
        this.lat = latitude;
        this.lng = longitude;
        this.#stop_id = stop_id;
        this.#stop_name = stop_name;
        this.#route_id = route_id;
        this.route_name = route_name;
        this.route_color = route_color;
        this.route_type = route_type;
        if (route_type === 2){ // 駅の場合
            this.#capacityOfBacterias = this.#capacityOfBacterias * 2;
            this.#metabolizingNutrient = this.#metabolizingNutrient * 2;
        }
        const uniqueStamps = Array.from(new Set(arrivalTimeStamps));
        uniqueStamps.sort((a, b) => a - b);
        this.#arrivalTimeStamps = uniqueStamps
        this.#lastTripTimeStampIdx = findLastIndex(currentTimeStamp,this.#arrivalTimeStamps); // 直近到達時刻のインデックス
        this.stationCoverageRadius = stationCoverageRadius;
        this.bacterias = new Set(); // 所属菌を保持するSet
        this.generateBacterias(this.#initialNoOfBacterias,this.#minimumInitialNutrient); // 菌の初期生成
    }


    /**
     * Stopに属する菌の生成
     * @param {number} noOfBacterias - 発生数
     * @param {number} initialNutrient - 初期栄養量
     * @returns 
     */
    generateBacterias(noOfBacterias,initialNutrient){
        const nutrients = divideNutrients(initialNutrient*noOfBacterias,noOfBacterias,varianceOfDivideNutrients);
        const randomPoints = generateRandomPoints( // Stop勢圏にランダムな点を生成
            this.lat,this.lng,stationCoverageRadius,noOfBacterias);
        randomPoints.map((point,index) => {
            const bacteria = new Bacteria(point.lat,point.lng,nutrients[index],this.route_color,this.#metabolizingNutrient,maxHealthPoint);
            this.bacterias.add(bacteria);
        });
    }    

    /**
     * Stop内部でのシミュレーションを開始する
     */
    simulate() {
        this.#innerTimer = setInterval(() => {
          const currentTimeStamp = Date.now(); // 現在のタイムスタンプ
          this.feed(); // 食事
          this.bacterias.forEach((bacteria) => {
            bacteria.breath(currentTimeStamp); // 呼吸
          })
        }, 1); // 0.1秒ごとに実行
      }



    /**
     * 細胞分裂
     */
    binaryFission(bacteria){
        if (bacteria.healtPoints < this.#minimumInitialNutrient * 2){
            return; // 分裂に必要な体力が閾値を下回っている．
        }
        if (bacteria.canSplit){
            Bacteria.split(bacteria,this); // 細胞分裂の実行
        }
    }

    /**
     * 菌の大量要因による自然死
     * @param {Bacteria} bacteria 
     */
    die(bacteria){
        if (bacteria.healtPoints <= 0){
            this.kill(bacteria);
        }
    }
    /**
     * 菌の殺害
     * @param {Bacteria} bacteria 
     */
    kill(bacteria){
        this.bacterias.delete(bacteria); // 参照を削除
        bacteria = null; // インスタンス自体を削除
    }

    /**
     * 自然淘汰
     * 大量に発生した場合，ランダムに菌が死滅する
     */
    naturalSelection() {
        if (this.bacterias.size <= this.#capacityOfBacterias) {
            return;
        }
        // バクテリアを配列に変換してランダムにソート
        const randomBacterias = Array.from(this.bacterias).sort(() => Math.random() - 0.5);
    
        // 必要な分だけバクテリアを削除
        const excess = this.bacterias.size - this.#capacityOfBacterias;
        for (let i = 0; i < excess; i++) {
            const bacteria = randomBacterias[i];
            this.kill(bacteria); // 残体力関係なく死滅
        }
    }

    /**
     * 栄養の到着発火を管理するトリガー
     * @param {number} currentTimeStamp - 現在のタイムスタンプ
     */
    checkArrival(currentTimeStamp) {
        // if (this.#stop_name = "新富町"){
        //     console.log("currentTimeStamp",formatTime(currentTimeStamp));
        // }
        // arrivalTimeStamps 内に currentTimeStamp が存在するか確認
        const lastIndex = this.#lastTripTimeStampIdx; // 現状の認識の直近到着トリップのindex
        const closestInfo = getClosestTimestamp(currentTimeStamp,this.#arrivalTimeStamps); // 最近傍点の情報

        if (lastIndex !== closestInfo.closestIndex){ /** 新たにtripの到達があったケース */
            this.#lastTripTimeStampIdx = closestInfo.closestIndex; // 最近傍のindexの更新
            /** 
             * ※ setInteravlではなく，アニメーションフレームに基づいたtimestamp更新だと
             * ミリ秒単位での時刻表との完全な一致が難しくなる．
             * 故に，タイムスタンプの更新が2分などになり，便をスキップするような更新にも対応する．
             * */
            let indexDifference; // 二つのインデックスの差分
            if (this.#lastTripTimeStampIdx > lastIndex){
                indexDifference = this.#lastTripTimeStampIdx - lastIndex;
            } else { // 日付をまたいでいる場合
                indexDifference = (this.#arrivalTimeStamps.length - lastIndex) + this.#lastTripTimeStampIdx;
            }

            /** 栄養量の操作 */
            this.#nutrientInStop += this.#defaultNutrient * indexDifference; // stopが持つ栄養量に追加
            /** 絶滅後の救済措置 */
            if (this.bacterias.size === 0){
                this.bring();
            }     
        }

        this.recognize(closestInfo.relativeTimeToClosest,closestInfo.relativeTimeToNext);
    }

    /**
     * 外から菌がもたらさせる
     */
    bring(){
        this.generateBacterias(3,10);
    }

    /**
     * 栄養の認知
     * @param {*} relativeTimeToClosest 超過OR等しい最近傍タイムスタンプまでの時間
     * @param {*} relativeTimeToNext  上記の次のタイムスタンプまでの時間
     */
    recognize(relativeTimeToClosest,relativeTimeToNext) {
        const halfPeriod = period / 2;
        let maxRecognizeRate = [1]; // 認知による拡大率（※最小値は1）

        // 到着前のタイムスタンプのスムージング結果
        if (relativeTimeToNext < halfPeriod) {
            const progress = (halfPeriod - relativeTimeToNext) / period; // 0 〜 1 に正規化
            const smoothingRate = this.customCurve(progress) * (maxAmplitude - 1) + 1;
            maxRecognizeRate.push(smoothingRate);
        } 
        // 到着後のタイムスタンプのスムージング結果
        if (relativeTimeToClosest < halfPeriod) {
            const progress = (halfPeriod + relativeTimeToClosest) / period; // 0 〜 1 に正規化
            const smoothingRate = this.customCurve(progress) * (maxAmplitude - 1) + 1;
            maxRecognizeRate.push(smoothingRate);
        } 

        this.applyRecognizeRate(Math.max(...maxRecognizeRate));
    }
    
    // 値の適用
    applyRecognizeRate(recognizeRate){
        this.bacterias.forEach((bacteria) => {
            bacteria.recognizeRate = recognizeRate;
        })
    }
    
    // カスタム曲線関数
    customCurve(progress) {
        if (progress < 0.5) {
            // 緩やかな増加部分
            return Math.pow(progress * 2, 2); // 0 ~ 1
        } else {
            // 三次ベジェを使用した減少部分
            const t = (progress - 0.5) * 2; // 0 ~ 1 に正規化
            return 1 - (t * t * (3 - 2 * t)); // 1 ~ 0
        }
    }
    

    /**
     * 栄養の到達時に栄養を配分する
     * 密集度を加味しない
     */
    feed(){
        // 場に栄養がなければスルー
        if (this.#nutrientInStop <= 0) {
            return;
        }
        // 場に菌がいなくてもスルー
        if (this.bacterias.size === 0) {
            return;
        }
        
        // 栄養の分配
        const feedingBacterias = Array.from(this.bacterias);
        if (this.#nutrientInStop <= 1){ // 少量の栄養量は均等に割り振る
            feedingBacterias.map((bacteria) => {
                bacteria.addNutrient(this.#nutrientInStop / feedingBacterias.length);
            })
            this.#nutrientInStop = 0;

        } else { // スムージングして分配する
            const maximumDistribution = this.#nutrientInStop * this.#distributionRate; // 同時点の最大分配総量
            const inidividualNutrients = divideNutrients(maximumDistribution,feedingBacterias.length); // 個々の最大分配量
            for (let i = 0; i < feedingBacterias.length; i++){
                const bacteria = feedingBacterias[i];
                const inidividualNutrient = Math.min(inidividualNutrients[i],maxHealthPoint); // 分配量
                this.#nutrientInStop -= inidividualNutrient; // Stopの保持分からマイナス
                bacteria.addNutrient(inidividualNutrient); // 菌に決定量を配布
            }
        }
    }

    /**
     * ctx向けに色を作成する．
     * @returns 
     */
    rgba(){
        // 6文字のHEX値を分解してRGBに変換
        const r = parseInt(this.route_color.slice(0, 2), 16);
        const g = parseInt(this.route_color.slice(2, 4), 16);
        const b = parseInt(this.route_color.slice(4, 6), 16);
        // RGBA形式の文字列を返す
        return `rgba(${r}, ${g}, ${b}, ${defaultOpacity})`;
    }

}

/**
 * 栄養量を任意の人数で分割し、散らばり具合を調整する
 * @param {number} total - 分割する栄養量
 * @param {number} numBacterias - 分割する人数
 * @param {number} variance - 散らばり具合（0で均等、値が大きいほどランダム性が増す）
 * @returns {number[]} - 各人に分配された栄養量の配列
 */
function divideNutrients(total, numBacterias, variance = varianceOfDivideNutrients) {

    if (numBacterias <= 0) throw new Error("人数は1以上である必要があります");
    if (total < 0) throw new Error("栄養量は0以上である必要があります");

    // ベースの均等配分
    const baseAmount = total / numBacterias;
    const results = new Array(numBacterias).fill(baseAmount);

    // 散らばりを追加
    let totalVariance = 0;

    for (let i = 0; i < numBacterias; i++) {
        const randomFactor = (Math.random() - 0.5) * 2; // -1から1のランダム値
        const adjustment = randomFactor * variance * baseAmount;
        results[i] += adjustment;
        totalVariance += adjustment;
    }

    // 合計がtotalになるように調整
    const adjustmentPerPerson = totalVariance / numBacterias;
    for (let i = 0; i < numBacterias; i++) {
        results[i] -= adjustmentPerPerson;
    }

    return results.map(amount => Math.min(Math.max(0, amount),maxHealthPoint)); // 負の値は0に
}

/**
 * 到着トリップのタイムスタンプのリストの中で，等しいOR超過しつつ，
 * 最近傍となるタイムスタンプのインデックスを取得する
 * @param {number} currentTimeStamp タイムスタンプ（秒）
 * @param {Array[number]} timestamps 到着時刻の配列 - タイムスタンプ（秒）
 * @returns 
 */
function findLastIndex(currentTimeStamp,timestamps){
    for (let i  = 0; i < timestamps.length; i++ ){
        if (currentTimeStamp < timestamps[i]){
            if (i === 0){
                return timestamps.length-1;    
            } else {
                return i-1;
            }
        }
    }
    throw new console.error("直近の到達トリップのindexが初期化できません");
}

export default Stop;